<template>
  <li 
    class="tree-node" 
    :class="{ 'is-inactive': !node.is_active }"
    :data-node-id="node.id"
    >
    <div class="node-content" :class="{ 'has-children': hasChildren }" :style="{ paddingLeft: depth * 24 + 'px' }">
      <!-- 添加層級標識線 -->
      <div v-if="depth > 0" class="level-lines" :style="{ left: (depth - 1) * 24 + 12 + 'px' }"></div>
      
      <span v-if="hasChildren" @click.stop="toggleExpand" class="toggle-icon" :class="{ 'is-expanded': isExpanded }">
        {{ isExpanded ? '▼' : '▶' }}
      </span>
      <span v-else class="toggle-icon-placeholder"></span>
      
      <span class="node-name" :class="'depth-' + depth">
        {{ node.name }}
        <span v-if="!node.is_active" class="inactive-tag">停用</span>
      </span>
      <span class="node-count">({{ node.product_count || 0 }})</span>
      <div class="node-actions">
        <button @click.stop="handleAddChildClick" class="action-button add-child-button" title="新增子項">+</button> 
        <button @click.stop="handleEditClick" class="action-button edit-button" title="編輯">編輯</button>
        <button @click.stop="handleDeleteClick" class="action-button delete-button" title="刪除">刪除</button>
      </div>
    </div>
    
    <div v-if="isExpanded && hasChildren" class="node-children">
      <draggable 
        v-model="node.children" 
        tag="ul" 
        item-key="id" 
        :group="{ name: 'categories', pull: true, put: true }"
        :animation="150"
        class="draggable-list"
        @end="onDragEnd"
        @add="onDragAdd"
        @remove="onDragRemove"
        @start="onDragStart"
        :disabled="dragDisabled"
      >
        <template #item="{ element: childNode }"> 
          <TreeNode 
            :node="childNode"
            :flat-categories="flatCategories" 
            :depth="depth + 1"
            @request-add-child="emitRequestAddChild" 
            @request-edit="emitRequestEdit"
            @request-delete="emitRequestDelete"
            @update="handleChildUpdate"
          />
        </template>
      </draggable>
    </div>
  </li> 
</template>

<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue';
import type { PropType } from 'vue';
import type { TreeNodeData, FlatCategory } from '../types'; 
import draggable from 'vuedraggable'; // 重新導入 draggable

const props = defineProps({
  node: {
    type: Object as PropType<TreeNodeData>,
    required: true
  },
  flatCategories: {
    type: Array as PropType<FlatCategory[]>,
    required: true
  },
  depth: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits([
  'request-add-child',
  'request-edit',
  'request-delete',
  'update' // 用於通知父層 vuedraggable 更新
]);

const isExpanded = ref(false);
// 從父組件注入展開/收起控制
const expandAllNodes = inject('expandAllNodes', ref(true));

// 監聽全局展開/收起控制
watch(expandAllNodes, (newValue) => {
  isExpanded.value = newValue;
});

const hasChildren = computed(() => props.node.children && props.node.children.length > 0);

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};

// 按鈕點擊處理
const handleAddChildClick = () => {
  emit('request-add-child', props.node);
};
const handleEditClick = () => {
  emit('request-edit', props.node);
};
const handleDeleteClick = () => {
  emit('request-delete', props.node);
};

// 新增：注入全局拖曳狀態控制
const globalDragDisabled = inject('globalDragDisabled', ref(false));
const dragDisabled = computed(() => globalDragDisabled.value);

// --- 拖曳事件處理 ---
const onDragStart = (event) => {
  console.log(`[TreeNode ${props.node.name}] Drag started:`, event);
};

const onDragEnd = (event) => {
  console.log(`[TreeNode ${props.node.name}] Child list @end event:`);
  
  // 添加錯誤處理
  try {
    // 更新子節點顯示順序
    updateChildrenDisplayOrder(props.node.children);
    
    // 當子列表拖放結束時，v-model 已經更新了 props.node.children
    emit('update'); 
  } catch (err) {
    console.error('拖曳處理出錯:', err);
  }
};

const onDragAdd = (event) => {
  console.log(`[TreeNode ${props.node.name}] Child list @add event:`);
  
  try {
    // 更新子節點顯示順序
    updateChildrenDisplayOrder(props.node.children);
    
    // 當有新節點被拖入時觸發
    emit('update');
  } catch (err) {
    console.error('處理拖入節點出錯:', err);
  }
};

const onDragRemove = (event) => {
  console.log(`[TreeNode ${props.node.name}] Child list @remove event:`);
  
  try {
    // 更新子節點顯示順序
    updateChildrenDisplayOrder(props.node.children);
    
    // 當有節點被拖出時觸發
    emit('update');
  } catch (err) {
    console.error('處理拖出節點出錯:', err);
  }
};

// 新增：更新子節點顯示順序的函數
function updateChildrenDisplayOrder(children) {
  if (!children || !Array.isArray(children)) return;
  
  // 更新子節點的顯示順序
  children.forEach((child, index) => {
    child.display_order = index;
  });
}

// --- 冒泡子組件的請求事件 ---
const emitRequestAddChild = (node) => emit('request-add-child', node);
const emitRequestEdit = (node) => emit('request-edit', node);
const emitRequestDelete = (node) => emit('request-delete', node);

// 新增：處理子節點更新事件並向上冒泡
const handleChildUpdate = () => {
  emit('update');
};

</script>

<style scoped>
.tree-node {
  list-style-type: none;
  margin: 2px 0;
  position: relative;
}

.node-content {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  transition: all 0.2s ease;
  position: relative;
  margin: 4px 0;
}

.node-content:hover {
  background-color: #f5f7fa;
  border-color: #c6e2ff;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}

/* 層級標識線 */
.level-lines {
  position: absolute;
  top: 50%;
  width: 24px;
  height: 1px;
  background-color: #dcdfe6;
  left: 0;
}

.level-lines::before {
  content: '';
  position: absolute;
  left: 0;
  top: -16px;
  width: 1px;
  height: 32px;
  background-color: #dcdfe6;
}

/* 不同深度的節點樣式 */
.depth-0 {
  font-weight: 600;
  color: #303133;
}

.depth-1 {
  color: #444;
}

.depth-2 {
  color: #555;
}

.depth-3, .depth-4, .depth-5 {
  color: #666;
}

.toggle-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  cursor: pointer;
  font-size: 12px;
  margin-right: 8px;
  border-radius: 3px;
  transition: all 0.2s;
}

.toggle-icon:hover {
  background-color: #ecf5ff;
  color: #409EFF;
}

.toggle-icon.is-expanded {
  transform: rotate(0deg);
}

.toggle-icon-placeholder {
  width: 20px;
  margin-right: 8px;
}

.node-name {
  flex-grow: 1;
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
  font-size: 14px;
}

.node-count {
  font-size: 12px;
  color: #909399;
  margin-right: 10px;
}

.node-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}

.node-content:hover .node-actions {
  opacity: 1;
}

.action-button {
  padding: 2px 8px;
  font-size: 12px;
  border-radius: 3px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.add-child-button {
  background-color: #ecf5ff;
  color: #409EFF;
  border-color: #d9ecff;
}

.add-child-button:hover {
  background-color: #409EFF;
  color: white;
}

.edit-button {
  background-color: #f0f9eb;
  color: #67c23a;
  border-color: #e1f3d8;
}

.edit-button:hover {
  background-color: #67c23a;
  color: white;
}

.delete-button {
  background-color: #fef0f0;
  color: #f56c6c;
  border-color: #fde2e2;
}

.delete-button:hover {
  background-color: #f56c6c;
  color: white;
}

.node-children {
  margin-left: 12px;
  position: relative;
}

.node-children::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 12px;
  width: 1px;
  background-color: #dcdfe6;
}

.draggable-list {
  padding-left: 0;
  margin: 0;
}

.inactive-tag {
  background-color: #fef0f0;
  color: #f56c6c;
  border: 1px solid #fbc4c4;
  padding: 0 6px;
  border-radius: 3px;
  font-size: 12px;
  margin-left: 8px;
  font-weight: normal;
}

/* 拖曳時的樣式 */
:deep(.sortable-ghost) {
  position: relative;
  opacity: 0.5;
  background: #e6f1fc !important;
  border: 1px dashed #409EFF !important;
}

:deep(.sortable-ghost)::after {
  content: '正在移動...';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: #409EFF;
  font-size: 12px;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.8);
  padding: 2px 8px;
  border-radius: 4px;
}

/* 拖曳時的目標區域樣式 */
:deep(.sortable-fallback) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: rotate(2deg);
  z-index: 9999;
}

/* 停用狀態樣式 */
.tree-node.is-inactive > .node-content {
  opacity: 0.7;
  background-color: #fafafa;
  border-style: dashed;
}

/* 拖曳時的視覺提示 */
.node-content.drag-over {
  border: 2px dashed #409EFF;
  background-color: #ecf5ff;
}

/* 可拖曳節點的視覺提示 */
.node-content {
  cursor: move;
}

.node-content:hover::before {
  content: '⋮';
  position: absolute;
  left: 4px;
  color: #909399;
  opacity: 0.5;
}

/* 拖曳時的目標區域提示 */
.node-children {
  min-height: 5px;
  position: relative;
}

.node-children::after {
  content: '';
  position: absolute;
  left: 24px;
  right: 0;
  height: 2px;
  background-color: transparent;
  transition: background-color 0.2s;
}

.node-children.drag-over::after {
  background-color: #409EFF;
}
</style>